import { Formik, type FormikHelpers } from 'formik';
import { Alert, Box, Link } from '@gusto/workbench';
import { postJson } from 'requests/http';
import GoogleSignupCta from 'packages/signup/components/signupForm/googleSignupCta';
import { loginBaseUrl } from 'constants/lead_form_utils';
import trackSignInClicked from 'packages/signup/analytics/trackSignInClicked';
import validationSchema from 'components/accountant_leads/validation_schema';
import { useEffect, useState } from 'react';
import { isFeatureEnabled } from 'helpers/gusto_ffe';
import useAccountantLead from './useAccountantLead';
import type { AccountantLeadFormDetails } from './types';
import { usePartnerSignupContext } from './partnersSignupProvider';
import PartnerFormDetails from './partner_form_details';
import getSubmitPayload from './getSubmitPayload';
import {
  trackPartnerSurveySignupSubmitFailed,
  trackPartnerSurveySignupSubmitSuccess,
} from '../analytics/tracking_helpers';

const PartnerFormFooter = ({
  oktaSSOEnabled,
  userInOktaSSO,
}: {
  oktaSSOEnabled: boolean;
  userInOktaSSO: boolean;
}) => (
  <>
    <div className="margin-bottom-15px lp-footer-signin text-align-center">
      {!userInOktaSSO && (
        <>
          Already use Gusto?{' '}
          <Link onClick={trackSignInClicked} href={loginBaseUrl}>
            Sign in here
          </Link>
          .
        </>
      )}
      {oktaSSOEnabled && (
        <p className="text-tiny margin-top-15px c-salt-700">
          By creating an account, you agree to our{' '}
          <Link href="https://gusto.com/legal/partners" target="_blank" className="c-salt-800">
            terms & conditions
          </Link>{' '}
          and{' '}
          <Link href="https://gusto.com/legal/privacy" target="_blank" className="c-salt-800">
            privacy policy
          </Link>
          .
        </p>
      )}
    </div>
  </>
);

const PartnerForm = ({ ssoDomains }: { ssoDomains: string[] }) => {
  const { loading, fromGoogleSSO, accountantLead, formSubmitUrl } = useAccountantLead();
  const { signupGoals, runPayrollForClients, offerAdvisoryServices, perkGoals } =
    usePartnerSignupContext();

  const [oktaSSOEnabled, setOktaSSOEnabled] = useState(false);
  const [userInOktaSSO, setUserInOktaSSO] = useState(false);

  useEffect(() => {
    const checkOktaSSOEnabled = async () => {
      const result = await isFeatureEnabled('accountants_okta_sso_enabled');
      if (result) {
        setOktaSSOEnabled(true);
      }
    };
    checkOktaSSOEnabled();
  }, []);

  if (loading) return null;

  const handleSubmit = (
    values: AccountantLeadFormDetails,
    formikOpts: FormikHelpers<AccountantLeadFormDetails>
  ) => {
    const surveyAnswers = {
      signupGoals,
      runPayrollForClients,
      offerAdvisoryServices,
      perkGoals,
    };

    const payload = getSubmitPayload(values, surveyAnswers);

    postJson(formSubmitUrl, payload)
      .then(res => {
        trackPartnerSurveySignupSubmitSuccess();
        window.location.href = res.redirect_url;
      })
      .catch(err => {
        const errorStatus = err?.cause?.status || '500';
        const errorMessage = err?.cause?.statusText || 'error from PartnerSignupForm';

        formikOpts.setStatus({ unknownError: true });
        formikOpts.setSubmitting(false);

        trackPartnerSurveySignupSubmitFailed(payload.accountant_lead, errorStatus, errorMessage);
      });
  };

  return (
    <Box maxWidth={632} width="100%">
      {!fromGoogleSSO && !userInOktaSSO && (
        <GoogleSignupCta
          googleSignupBaseUrl="/google_sso/accountant_signup/new"
          googleButtonLabel="Sign up with Google"
          fromPartner
        />
      )}

      <Formik
        initialValues={{
          accountant_lead: {
            first_name: accountantLead?.first_name || '',
            last_name: accountantLead?.last_name || '',
            email: accountantLead?.email || '',
            accounting_firm_name: accountantLead?.accounting_firm_name || '',
            website: '',
            number_companies_bucket: '',
            number_payroll_services_clients_bucket: '',
            phone: '',
          },
          terms_acceptance: false,
        }}
        onReset={() => setUserInOktaSSO(false)}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(!oktaSSOEnabled)}
      >
        {({ status }) => (
          <>
            <PartnerFormDetails
              oktaSSOEnabled={oktaSSOEnabled}
              ssoDomains={oktaSSOEnabled ? ssoDomains : []}
              userInOktaSSO={userInOktaSSO}
              setUserInOktaSSO={setUserInOktaSSO}
            />
            {status?.unknownError && (
              <Alert label="Error" severity="error">
                We&apos;re experiencing technical issues, Please try again later.
              </Alert>
            )}
          </>
        )}
      </Formik>

      <PartnerFormFooter oktaSSOEnabled={oktaSSOEnabled} userInOktaSSO={userInOktaSSO} />
    </Box>
  );
};

export default PartnerForm;
